import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { trpc } from "@/lib/trpc";
import Results from "./Results";
import { AgencyLogo } from "@/components/AgencyLogo";
import { BarometerNav } from "@/components/BarometerNav";
import { CrossSelling } from "@/components/CrossSelling";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";

const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

const C = {
  accent: "#6d0000",
  accentDark: "#4a0000",
  accentLight: "#6d000012",
  teal: "#115751",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

type PreviewData = {
  url: string;
  page_title: string;
  total_score: number;
  max_score: number;
  reifegrad: { name: string; desc: string; color: string; prozent: number };
  dimension_scores: Record<string, { score: number; max: number; label: string }>;
  top3_tipps: Array<{ dimension: string; kriterium: string; prioritaet: string }>;
};

function getScoreColor(pct: number) {
  if (pct >= 80) return "#16a34a";
  if (pct >= 60) return "#65a30d";
  if (pct >= 40) return "#d97706";
  return "#dc2626";
}

function LoadingScreen({ url }: { url: string }) {
  const steps = [
    { label: "Startseite laden & Screenshot erstellen" },
    { label: "Visuelle Sensorik analysieren" },
    { label: "Sprache & emotionale Wörter prüfen" },
    { label: "Haptik & Interaktion bewerten" },
    { label: "Vertrauen & Trust-Signale prüfen" },
    { label: "Sensorik-Score berechnen" },
  ];
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % steps.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
      <div style={{ height: "4px", backgroundColor: C.accent, position: "fixed", top: 0, left: 0, right: 0 }} />
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: C.accentLight }}>
            <span className="text-2xl">🔍</span>
          </div>
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
            Sensorik-Barometer™ analysiert
          </p>
          <h2 className="font-black uppercase text-2xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Vorschau wird erstellt
          </h2>
          <p className="text-gray-500 text-sm truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{url}</p>
        </div>
        <div className="space-y-2 text-left mb-8">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5"
              style={{
                backgroundColor: i === activeStep ? C.accentLight : "transparent",
                borderLeft: i === activeStep ? `3px solid ${C.accent}` : "3px solid transparent"
              }}>
              <span className="text-sm" style={{ color: i <= activeStep ? C.text : C.muted, fontFamily: "'DM Sans', sans-serif" }}>{step.label}</span>
              {i < activeStep && <span className="ml-auto text-green-600 text-sm">✓</span>}
              {i === activeStep && <span className="ml-auto text-xs animate-pulse" style={{ color: C.accent }}>läuft …</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewResults({ data, onReset, onBuy, onBuyVideoCall }: { data: PreviewData; onReset: () => void; onBuy: () => void; onBuyVideoCall: () => void }) {
  const dims = Object.entries(data.dimension_scores);
  const scoreColor = getScoreColor(data.total_score);
  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ height: "4px", backgroundColor: C.accent }} />
      <BarometerNav current="/sensorik" accentColor={C.accent} />
      <div className="max-w-2xl mx-auto w-full px-6 py-10">
        <div className="text-center mb-8">
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
            Sensorik-Barometer™ · Persönliche Analyse von Larissa
          </p>
          <h2 className="font-black uppercase text-3xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {data.page_title}
          </h2>
          <p className="text-gray-400 text-sm truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{data.url}</p>
        </div>

        <div className="border border-gray-200 p-8 text-center mb-6" style={{ backgroundColor: C.bgGray }}>
          <p className="text-xs font-bold tracking-widest uppercase mb-3 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorik-Gesamtscore</p>
          <div className="text-7xl font-black mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: scoreColor }}>
            {data.total_score}
            <span className="text-3xl text-gray-400">/100</span>
          </div>
          <div className="inline-block px-4 py-1.5 text-sm font-bold uppercase tracking-widest" style={{ backgroundColor: scoreColor + "20", color: scoreColor, fontFamily: "'DM Sans', sans-serif" }}>
            {data.reifegrad.name}
          </div>
          <p className="text-gray-500 text-sm mt-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>{data.reifegrad.desc}</p>
        </div>

        <div className="mb-6">
          <p className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Deine Sensorik-Dimensionen</p>
          <div className="space-y-3">
            {dims.map(([key, dim]) => {
              const pct = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const dc = getScoreColor(pct);
              return (
                <div key={key} className="border border-gray-200 p-4 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.label}</span>
                    <span className="font-black text-sm" style={{ color: dc, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max}</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200">
                    <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: dc }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-2 border-dashed border-gray-300 p-6 mb-6 text-center" style={{ backgroundColor: "#fafafa" }}>
          <div className="text-2xl mb-2">🔒</div>
          <p className="font-bold text-sm mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {data.top3_tipps.length} konkrete Maßnahmen identifiziert
          </p>
          <p className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Die vollständige Analyse mit allen Empfehlungen, Prioritäten und dem persönlichen PDF-Report von Larissa ist in der Vollversion enthalten.
          </p>
        </div>

        {/* Buchungsoptionen: zwei Karten nebeneinander */}
        <p className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt — Wähle deine Option</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Option 1: Nur Analyse */}
          <div className="border-2 border-gray-200 p-6 flex flex-col">
            <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
            <h3 className="font-black uppercase text-xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
            <p className="text-gray-600 text-sm mb-4 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.
            </p>
            <div className="text-2xl font-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>
              {formatPrice(BAROMETER_PRICES.sensorik)}
            </div>
            <button
              onClick={onBuy}
              className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-3 transition-opacity hover:opacity-90"
              style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}
            >
              Analyse buchen
            </button>
          </div>
          {/* Option 2: Analyse + VideoCall */}
          <div className="border-2 p-6 flex flex-col relative" style={{ borderColor: C.accent, backgroundColor: C.accent + "08" }}>
            <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
            <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
            <h3 className="font-black uppercase text-xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
            <p className="text-gray-600 text-sm mb-4 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.
            </p>
            <div className="text-2xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>
              {formatPrice(BAROMETER_MIT_VIDEOCALL)}
            </div>
            <p className="text-xs text-gray-400 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
            <button
              onClick={onBuyVideoCall}
              className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-3 transition-opacity hover:opacity-90"
              style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}
            >
              Analyse + VideoCall buchen
            </button>
          </div>
        </div>

        <div className="text-center">
          <button onClick={onReset} className="text-xs text-gray-400 hover:text-gray-600 underline" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Andere URL analysieren
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SensorikHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "Sensorik-Barometer™ – Warum verlassen Besucher deine Website? | WERBEAGENTUR Larissa Girenko",
    description: "74 % der Besucher verlassen eine Website wegen des Gefühls, nicht des Preises. 100+ Sensorik-Kriterien. Vollanalyse ab 97 € – Kostenlose Vorschau sofort.",
    path: "/sensorik-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [branche, setBranche] = useState("");
  const [analyzingUrl, setAnalyzingUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [analysisData, setAnalysisData] = useState<Record<string, unknown> | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const previewMutation = trpc.barometer.analyzePreview.useMutation({
    onSuccess: (data) => {
      setIsLoading(false);
      if (data.success && data.data) setPreviewData(data.data as PreviewData);
      else setAnalysisError(data.error || "Vorschau fehlgeschlagen.");
    },
    onError: (err) => {
      setIsLoading(false);
      setAnalysisError(err.message);
    },
  });

  const analyzeAdmin = trpc.barometer.analyzeAdmin.useMutation({
    onSuccess: (data) => {
      setIsLoading(false);
      if (data.success && data.data) setAnalysisData(data.data as Record<string, unknown>);
      else setAnalysisError(data.error || "Analyse fehlgeschlagen.");
    },
    onError: (err) => {
      setIsLoading(false);
      setAnalysisError(err.message);
    },
  });

  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank");
    },
    onError: (err) => toast.error(err.message),
  });

  const handlePreview = () => {
    if (!url.trim()) return;
    setAnalysisError(null);
    setAnalyzingUrl(url.trim());
    setIsLoading(true);
    previewMutation.mutate({ url: url.trim(), branche: branche || undefined });
  };

  const handleAdminAnalysis = () => {
    if (!url.trim()) return;
    setAnalysisError(null);
    setAnalyzingUrl(url.trim());
    setIsLoading(true);
    analyzeAdmin.mutate({ url: url.trim(), branche: branche || undefined });
  };

  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "sensorik_analyse",
      targetUrl: previewData?.url || url.trim() || undefined,
      returnPath: "/sensorik",
    });
  };

  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "sensorik_mit_videocall",
      targetUrl: previewData?.url || url.trim() || undefined,
      returnPath: "/sensorik",
    });
  };

  const handleReset = () => {
    setUrl(""); setBranche(""); setIsLoading(false);
    setPreviewData(null); setAnalysisData(null); setAnalysisError(null);
    previewMutation.reset(); analyzeAdmin.reset();
  };

  if (analysisData) return <Results data={analysisData} onReset={handleReset} />;
  if (previewData) return <PreviewResults data={previewData} onReset={handleReset} onBuy={handleBuy} onBuyVideoCall={handleBuyVideoCall} />;
  if (isLoading) return <LoadingScreen url={analyzingUrl} />;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ height: "4px", backgroundColor: C.accent }} />
      <BarometerNav current="/sensorik" accentColor={C.accent} />

      {/* ── HERO: alles above the fold ── */}
      <section className="w-full bg-black">
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-12">
          <div className="flex flex-col md:flex-row md:items-start md:gap-10">

            {/* Linke Spalte: Text + Formular + CTA */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
                Sensorik-Barometer™ · WERBEAGENTUR Larissa Girenko
              </p>
              <h1 className="font-black uppercase leading-none mb-3 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.4rem, 5.5vw, 4rem)" }}>
                Verliert deine Website<br />täglich Kunden?
              </h1>
              <p className="text-gray-300 text-sm leading-relaxed mb-4 max-w-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                74 % der Besucher verlassen eine Website, weil sie sich nicht richtig <em>anfühlt</em> — nicht wegen des Preises. Ich messe das mit 100+ Sensorik-Kriterien und zeige dir genau, was dich Kunden kostet.
              </p>

              {/* Social Proof */}
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <a href="https://www.google.com/maps/search/WERBEAGENTUR+Larissa+Girenko" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 border border-white/20 hover:border-white/40 transition-colors">
                  <span className="text-yellow-400 text-xs">★★★★★</span>
                  <span className="text-white text-xs font-bold" style={{ fontFamily: "'DM Sans', sans-serif" }}>5,0 · Google</span>
                </a>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>17+ Jahre Erfahrung</span>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Harvard & NNG-Methodik</span>
              </div>

              {/* URL-Eingabe */}
              <div className="mb-3">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (isAdminUser ? handleAdminAnalysis() : handlePreview())}
                  placeholder="https://deine-website.de"
                  className="w-full bg-white/10 border border-white/30 text-white placeholder-gray-500 px-4 py-3 text-sm focus:outline-none focus:border-white/60"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                />
              </div>

              {analysisError && (
                <div className="border-l-4 border-red-500 bg-red-900/30 px-4 py-3 mb-3">
                  <p className="text-sm text-red-300" style={{ fontFamily: "'DM Sans', sans-serif" }}>{analysisError}</p>
                </div>
              )}

              {isAdminUser ? (
                <div className="space-y-2">
                  <div className="border border-dashed border-white/20 px-4 py-2 text-center">
                    <p className="text-xs text-gray-400 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>🔑 Admin-Modus: Vollanalyse</p>
                  </div>
                  <button
                    onClick={handleAdminAnalysis}
                    disabled={!url.trim() || analyzeAdmin.isPending}
                    className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-4 transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {analyzeAdmin.isPending ? "Analysiert …" : "Sensorik-Vollanalyse starten →"}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handlePreview}
                  disabled={!url.trim() || previewMutation.isPending}
                  className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-4 transition-opacity hover:opacity-90 disabled:opacity-40"
                  style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}
                >
                  {previewMutation.isPending ? "Analysiert …" : "URL eingeben & Anfrage senden →"}
                </button>
              )}

              <div className="flex flex-wrap gap-3 mt-3">
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>✓ Persönlich von Larissa analysiert</span>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>✓ Kein Account nötig</span>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>✓ Lieferung in 72h</span>
              </div>
            </div>

            {/* Rechte Spalte: Portrait + Stats (Desktop only) */}
            <div className="hidden md:flex flex-col items-center gap-4 flex-shrink-0" style={{ width: "180px" }}>
              <div className="relative overflow-hidden" style={{ width: "160px", height: "160px" }}>
                <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.7) 100%)" }} />
                <div className="absolute top-0 left-0 right-0 h-0.5 z-20" style={{ backgroundColor: C.accent }} />
                <img src={PORTRAIT_IMG} alt="Larissa Girenko – Sensorik-Marketing-Expertin"
                  className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-full space-y-2">
                {[
                  { val: "6", label: "Dimensionen" },
                  { val: "100+", label: "Kriterien" },
                  { val: "72h", label: "Lieferzeit" },
                ].map((s) => (
                  <div key={s.label} className="border-l-2 pl-3" style={{ borderColor: C.accent }}>
                    <p className="text-white font-bold text-lg leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{s.val}</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trust-Strip */}
      <div className="border-b border-gray-200 py-4 px-6" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-yellow-500 text-sm">★★★★★</span>
            <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>5,0 · 3 Google-Bewertungen</span>
          </div>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>17+ Jahre Erfahrung</span>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>Persönliche Analyse von Larissa</span>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>Harvard & NNG-Methodik</span>
        </div>
      </div>

      {/* Was analysiert wird */}
      <section className="max-w-5xl mx-auto w-full px-6 py-12">
        <div className="text-center mb-8">
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>6 Dimensionen · 100+ Kriterien</p>
          <h2 className="font-black uppercase text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Was ich für dich analysiere</h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { icon: "👁", title: "Visueller Eindruck", desc: "Farbpsychologie, Bildsprache, visuelle Hierarchie" },
            { icon: "💬", title: "Sprache & Emotion", desc: "Emotionale Trigger, Du-Ansprache, Storytelling" },
            { icon: "🎵", title: "Auditive Sensorik", desc: "Video-Content, Audio-Elemente, Sound-Branding" },
            { icon: "✋", title: "Haptik & Interaktion", desc: "Animationen, CTAs, Mobile-Touch-Erlebnis" },
            { icon: "🧠", title: "Assoziative Sensorik", desc: "Marken-Assoziationen, Konsistenz, Storytelling" },
            { icon: "🛡", title: "Vertrauen & Trust", desc: "Social Proof, Auszeichnungen, Kontaktdaten" },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 p-4 border border-gray-200 bg-white">
              <span className="text-xl flex-shrink-0">{item.icon}</span>
              <div>
                <p className="font-bold text-sm mb-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.title}</p>
                <p className="text-xs text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Über Larissa */}
      <section className="w-full bg-black py-12 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10">
          <div className="flex-shrink-0">
            <img src={PORTRAIT_IMG} alt="Larissa Girenko" className="object-cover object-top"
              style={{ width: "160px", height: "200px" }} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
              Warum persönliche Analyse?
            </p>
            <h2 className="font-black uppercase text-white text-3xl mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              Emotionale Wirkung ist messbar
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Automatisierte Tools messen Technik — ich messe, wie deine Website sich anfühlt. Basierend auf Harvard, der Nielsen Norman Group und aktuellem Neuromarketing-Forschungsstand 2025/2026.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[
                { value: "17+", label: "Jahre Erfahrung" },
                { value: "6", label: "Dimensionen" },
                { value: "100+", label: "Kriterien geprüft" },
                { value: "72h", label: "Lieferzeit" },
              ].map((stat) => (
                <div key={stat.label} className="border-l-2 pl-4" style={{ borderColor: C.accent }}>
                  <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{stat.value}</p>
                  <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stat.label}</p>
                </div>
              ))}
            </div>
            <span className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analyse ab 97 € · Lieferung in 72h</span>
          </div>
        </div>
      </section>

      {/* Cross-Selling */}
      <CrossSelling
        headline="Sensorik ist der Anfang"
        subline="Kombiniere das Sensorik-Barometer mit diesen Analysen für das vollständige Bild."
        items={[
          {
            icon: "/manus-storage/33_d51b7819.svg",
            title: "Conversion-Barometer™",
            description: "Warum kaufen Besucher nicht? Entdecke die psychologischen Hebel, die über Kauf oder Absprung entscheiden.",
            price: formatPrice(BAROMETER_PRICES.conversion),
            href: "/conversion-barometer",
            accent: "#b45309",
            badge: "Ergänzt Sensorik",
          },
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "100+ SEO-Kriterien — von Title-Tags bis E-E-A-T. Damit dich auch Suchmaschinen finden.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
          },
          {
            icon: "/manus-storage/29_7f3bb394.svg",
            title: "Datenschutz-Barometer™",
            description: "DSGVO, Cookie-Consent, SSL — rechtlich auf der sicheren Seite, bevor es teuer wird.",
            price: formatPrice(BAROMETER_PRICES.datenschutz),
            href: "/datenschutz-barometer",
            accent: "#0f766e",
          },
        ]}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <AgencyLogo withLogo size="sm" />
          <div className="flex flex-wrap items-center gap-4 justify-center">
            <a href="/" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Übersicht</a>
            <a href="/lokal" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Lokal</a>
            <a href="/geo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>GEO</a>
            <a href="/seo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>SEO</a>
            <a href="/conversion-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Conversion</a>
            <a href="/impressum" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Impressum</a>
            <a href="/datenschutz" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Datenschutz</a>
          </div>
          <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}
